import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { Form, Link, useSearchParams } from "react-router";

import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";

import { useURL } from "~/contexts";
import { useDebounce } from "~/lib/ui";

import { useSearchContext } from "./meganav/search-context";
import { Button } from "./ui/button";

export const SearchInput = ({
  suggestions,
}: {
  className?: string;
  suggestions?: (text: string) => ReactNode;
  isMobile?: boolean;
}) => {
  const url = useURL();
  const {
    searchState,
    setSearchState,
    isSuggestionsOpen,
    setIsSuggestionsOpen,
  } = useSearchContext();

  const [searchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedValue = useDebounce(searchState.value.trim(), 120);
  const debouncedLink = url(
    debouncedValue && `/search?q=${encodeURIComponent(debouncedValue)}`,
  );

  useEffect(() => {
    const blurrableElements = document.querySelectorAll("#blurrable-content");
    const headerContent = document.querySelector("#nav-content");

    if (blurrableElements.length)
      blurrableElements.forEach(
        e => e.classList.remove("blur", "pointer-events-none"),
        headerContent?.classList.remove(
          "hover:bg-transparent",
          "focus-within:bg-transparent",
        ),
      );
  });

  const suggestionsComponent = useMemo(() => {
    if (!suggestions) return null;
    return suggestions(debouncedValue);
  }, [suggestions, debouncedValue]);

  const clearInput = (focusInput = true) => {
    if (searchInputRef?.current) {
      searchInputRef.current.value = "";
      focusInput && searchInputRef.current.focus();
      setIsSuggestionsOpen(false);
    }
  };

  return (
    <>
      <div className="relative w-1/2 gap-1 pb-6 sm-max:w-full">
        <Form
          method="get"
          action={url("/search")}
          className="relative flex w-full justify-between gap-2"
        >
          <div className="absolute left-3 top-[20px] flex -translate-y-1/2 items-center sm:left-4">
            <MagnifyingGlassIcon
              className="h-5 w-5 transition-[background-color]"
              aria-hidden="true"
            />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            name="q"
            maxLength={50}
            onChange={e => {
              const newValue = e.target.value;
              setSearchState({ value: newValue });
              setIsSuggestionsOpen(true);
            }}
            id="search"
            defaultValue={
              searchParams.get("q")
                ? searchParams.get("q")?.toString()
                : searchState.value
            }
            autoComplete="off"
            className={`xs:text-sm h-10 w-full rounded-full border border-gray-300 bg-gray-200 pl-12 pr-16 text-xs text-neutral-900 focus:outline-none md:text-base`}
            placeholder="Search for a product..."
          />
          <Button
            variant="default"
            className="text-base font-semibold text-white"
            type="submit"
          >
            Search
          </Button>
        </Form>
        <div className="absolute right-28 top-3">
          <Button
            variant="linkInline"
            onClick={() => {
              clearInput();
            }}
            className="no-underline underline-offset-0 hover:no-underline"
          >
            <p className="text-xs font-normal text-neutral-400">
              <Cross2Icon className={`h-[18px] w-[18px]`} />
            </p>
          </Button>
        </div>
        {debouncedLink && (
          <Link to={debouncedLink} prefetch="render" className="hidden">
            Search: {debouncedLink}
          </Link>
        )}
      </div>
      {suggestionsComponent && isSuggestionsOpen && (
        <div className="flex w-full flex-col rounded-lg bg-page-background">
          {suggestionsComponent}
        </div>
      )}
    </>
  );
};
